import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  SparklesIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { PageHeader } from '@/components/layout';
import {
  Button,
  Card,
  CardHeader,
  Input,
  Select,
  Badge,
  getStatusBadgeVariant,
  ContentLoader,
  ErrorSummary,
  BackButton,
} from '@/components/common';
// Imported by path rather than through components/common/index.ts, which is
// outside this task's file domain.
import {
  ContactAvatarPicker,
  avatarUploadErrorMessage,
  type ContactAvatarImage,
} from '@/components/common/ContactAvatar';
// B2: the City input was free text, and a name the seeded `geography.City`
// table does not hold was discarded server-side with no error at all. The
// picker posts an id, so what the user chose is what is stored. Imported by
// path for the same reason as the two above.
import { SearchableSelect } from '@/components/common/SearchableSelect';
import { AddressFields } from '@/components/forms/AddressFields';
import { useCityLookup, useStateLookup } from '@/hooks/useGeography';
import { useCustomer, useCreateCustomer, useUpdateCustomer } from '@/hooks';
import api, { getErrorMessage } from '@/services/api';
// Imported by path rather than through services/index.ts, which is outside
// this task's file domain. Same client the KYC panel on the detail page uses --
// one upload implementation, so the two screens cannot drift about what a
// successful upload means.
import {
  CUSTOMER_KYC_DOCUMENT_TYPES,
  KYC_FIELD_LABELS,
  customerKycService,
  type ExtractedField,
} from '@/services/kycDocuments';
import { useAuthStore } from '@/store/auth';
import { useCan } from '@/hooks/useEmployeeRights';
import { applyServerErrors } from '@/utils/formErrors';
import { phoneSchema, digitsOnlyOnChange, nameKeepOnChange, optionalEmailSchema, allowUnchangedEmails } from '@/utils/validation';
import { personNameProblem } from '@/utils/format';
import { kycStatusLabel } from '@/utils';
import {
  customerTypeOptions,
  salutationCodesFor,
  salutationOptionsFor,
  IDENTITY_FIELDS_BY_TYPE,
  getIdentityFields,
  type CustomerFieldKey,
  BASIC_FIELDS_BY_TYPE,
  getBasicFields,
  segmentOptions,
  genderOptions,
  maritalStatusOptions,
} from '@/utils/customerBasicInfo';

// ---------------------------------------------------------------------------
// Wave D1b: per-customer_type Basic Information + Identity & Tax field SCHEMA.
//
// Only Individual/Corporate/Proprietorship are built out (Phase 1). HUF/Trust/
// Partnership/LLP are a documented Phase-2 extension point: their Customer
// Type option remains selectable, but BASIC_FIELDS_BY_TYPE/IDENTITY_FIELDS_BY_TYPE
// fall back to the Individual field set (getBasicFields/getIdentityFields below)
// so those types stay fully usable -- not blank/broken -- until Phase 2 gives
// them a dedicated config. Their SALUTATION_CODES_BY_TYPE entries are defined
// now (backend choices already exist) since that's essentially free.
//
// PHASE1_TYPES/isPhase1Type and the salutation config live in
// '@/utils/customerBasicInfo' (Wave E) -- shared with PolicyCreatePage's
// minimal Policy Holder subset so both stay in lockstep with the backend's
// Customer.CUSTOMER_TYPE_CHOICES / SALUTATION_CHOICES.
// ---------------------------------------------------------------------------


// BASIC_FIELDS_BY_TYPE/getBasicFields and IDENTITY_FIELDS_BY_TYPE/
// getIdentityFields now live in '@/utils/customerBasicInfo' so other pages can
// render/validate the SAME per-type field set this form captures.

// Every field that appears in at least one type's config -- used to clear
// values (and their errors) that belong to a type the form just switched
// away from.
const ALL_TOGGLEABLE_FIELDS = Array.from(
  new Set<CustomerFieldKey>([
    ...Object.values(BASIC_FIELDS_BY_TYPE).flat(),
    ...Object.values(IDENTITY_FIELDS_BY_TYPE).flat(),
  ])
);

const customerSchema = z
  .object({
    customer_type: z.enum(['individual', 'corporate', 'huf', 'trust', 'partnership', 'llp', 'proprietorship']),
    segment: z.enum(['standard', 'premium', 'high_value', 'vip', 'corporate']).optional(),
    // Loose (not a literal enum): the option set is type-dependent (see
    // SALUTATION_CODES_BY_TYPE) and the backend already validates the real
    // choices via its own ChoiceField.
    salutation: z.string().optional(),
    first_name: z.string().optional(),
    middle_name: z.string().optional(),
    last_name: z.string().optional(),
    date_of_birth: z.string().optional(),
    gender: z.enum(['male', 'female', 'other']).optional(),
    marital_status: z.enum(['single', 'married', 'divorced', 'widowed', '']).optional(),
    occupation: z.string().optional(),
    annual_income: z.string().optional(),
    company_name: z.string().optional(),
    company_type: z.string().optional(),
    incorporation_date: z.string().optional(),
    cin_number: z.string().optional(),
    industry: z.string().optional(),
    nature_of_business: z.string().optional(),
    annual_turnover: z.string().optional(),
    num_employees: z.string().optional(),
    website: z.string().url('Invalid URL').optional().or(z.literal('')),
    email: optionalEmailSchema,
    phone: phoneSchema,
    alternate_phone: z.string().optional(),
    pan_number: z.string().optional(),
    aadhaar_number: z.string().optional(),
    gst_number: z.string().optional(),
    passport_number: z.string().optional(),
    driving_license: z.string().optional(),
    address_line1: z.string().optional(),
    address_line2: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    pincode: z.string().regex(/^\d{0,6}$/, 'Pincode must be up to 6 digits').optional(),
  })
  .superRefine((data, ctx) => {
    const basicFields = getBasicFields(data.customer_type);
    const isCorporate = data.customer_type === 'corporate';

    // Person names: letters of any script, space, hyphen, apostrophe, dot. Digits
    // are rejected with a visible message instead of being silently dropped (DQ-640).
    if (!isCorporate) {
      for (const key of ['first_name', 'middle_name', 'last_name'] as const) {
        const problem = personNameProblem(data[key]);
        if (problem) ctx.addIssue({ code: z.ZodIssueCode.custom, message: problem, path: [key] });
      }
    }
    // "Name" requirement varies by type: Corporate has no visible person-name
    // input (see BASIC_FIELDS_BY_TYPE) -- Company Name is its core identifying
    // field instead. Every other type still requires First Name, mirroring
    // the "name + phone remain the core required" rule from the brief.
    if (!isCorporate && !data.first_name?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'First name is required',
        path: ['first_name'],
      });
    }
    // last_name is only required when it's actually visible for the selected
    // customer_type (Individual, Proprietorship). Keeps validation in
    // lockstep with getBasicFields() so hidden fields are never required.
    if (basicFields.includes('last_name') && !data.last_name?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Last name is required',
        path: ['last_name'],
      });
    }
    // Company/Business Name is the core identifying field for Corporate and
    // Proprietorship (reused mapping -- see BASIC_FIELDS_BY_TYPE).
    if (basicFields.includes('company_name') && !data.company_name?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: isCorporate ? 'Company Name is required' : 'Business Name is required',
        path: ['company_name'],
      });
    }
  });

type CustomerFormData = z.infer<typeof customerSchema>;

/**
 * F2: profile-picture upload/replace/remove for an existing contact.
 *
 * Both mutations hit the avatar action `CustomerViewSet` already exposes
 * (`POST`/`DELETE /customers/{id}/avatar/`) — no new endpoint. Scoping is the
 * endpoint's: it resolves the contact through the broker-filtered
 * `get_queryset`, so another tenant's id is a 404 there and cannot be reached
 * from here regardless of what this page sends.
 *
 * It renders only while editing. The Add Contact screen has no id yet, so it
 * uses NewContactPhotoCard below instead -- same widget, but the file is held
 * locally and posted to this same endpoint once the create returns an id. That
 * keeps the "no orphan asset if the create fails validation" property that kept
 * a picture off the create form in the first place, while queue #230's actual
 * complaint (having to save, reopen in Edit, and upload) goes away.
 */
function ContactPhotoCard({
  customerId,
  name,
  image,
}: {
  customerId: string;
  name: string;
  image?: ContactAvatarImage | null;
}) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const refresh = () => {
    // Both the detail/edit query and every cached list page carry a copy of
    // avatar_image, so a stale list would keep showing the old face.
    queryClient.invalidateQueries({ queryKey: ['customer', customerId] });
    queryClient.invalidateQueries({ queryKey: ['customers'] });
  };

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      // The action reads request.FILES['file'] (falling back to 'avatar').
      form.append('file', file);
      const { data } = await api.post(`/customers/${customerId}/avatar/`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        // Optimisation and rendition generation are synchronous server-side
        // (there is no Celery here), so a phone-camera JPEG can outlast the
        // client's 30s default.
        timeout: 180000,
      });
      return data;
    },
    onSuccess: () => {
      setError(null);
      refresh();
      toast.success('Profile picture updated');
    },
    onError: (err) => setError(avatarUploadErrorMessage(err)),
  });

  const removeMutation = useMutation({
    mutationFn: async () => {
      const { data } = await api.delete(`/customers/${customerId}/avatar/`);
      return data;
    },
    onSuccess: () => {
      setError(null);
      refresh();
      toast.success('Profile picture removed');
    },
    onError: (err) => setError(avatarUploadErrorMessage(err)),
  });

  return (
    <Card>
      <CardHeader
        title="Profile Picture"
        subtitle="Shown on the contacts list, this contact's record and their own dashboard."
      />
      <ContactAvatarPicker
        contactId={customerId}
        name={name}
        image={image}
        onSelect={(file) => {
          setError(null);
          uploadMutation.mutate(file);
        }}
        onRemove={() => removeMutation.mutate()}
        isBusy={uploadMutation.isPending || removeMutation.isPending}
        error={error}
      />
    </Card>
  );
}

/**
 * Queue #230: the same picker on the ADD contact screen.
 *
 * There is no contact id before the create returns, so this holds the chosen
 * File in page state and shows a local object-URL preview; CustomerFormPage
 * uploads it to `/customers/{newId}/avatar/` in the create's onSuccess. Nothing
 * is sent to the server until the contact exists, so a create that fails
 * validation leaves no asset behind -- and the user still picks the picture
 * once, in the place they expect, before pressing Save.
 */
function NewContactPhotoCard({
  file,
  onSelect,
  onClear,
}: {
  file: File | null;
  onSelect: (file: File) => void;
  onClear: () => void;
}) {
  // Object URLs are leaked unless revoked; recreate one only when the File
  // itself changes and revoke the previous on unmount/replace.
  //
  // Guarded because this runs in an effect during commit: an environment
  // without URL.createObjectURL (jsdom, and any browser that blocks it) would
  // otherwise throw there and unmount the whole Add Contact form over a
  // thumbnail. Losing the preview is the right degradation -- the file is still
  // held in state and still uploads.
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!file || typeof URL.createObjectURL !== 'function') {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  return (
    <Card>
      <CardHeader
        title="Profile Picture"
        subtitle="Optional. Uploaded as soon as the contact is created."
      />
      <ContactAvatarPicker
        name={null}
        image={previewUrl ? ({ src: previewUrl } as ContactAvatarImage) : null}
        onSelect={onSelect}
        onRemove={file ? onClear : undefined}
        hint="JPG, PNG or WebP, up to 15 MB. Saved with the contact when you press Create."
      />
    </Card>
  );
}

// ── KYC documents on the Add Contact screen (item 4) ─────────────────────────

/**
 * B1: the fields a KYC reading can fill on THIS form.
 *
 * `FIELD_READERS` (backend/apps/customers/kyc.py) can also produce `voter_id`,
 * which the Contacts form has no input for. That is listed back to the user as
 * "read but not on this form" rather than dropped silently -- a value that
 * appears nowhere and is mentioned nowhere is indistinguishable from one that
 * was never read.
 */
const KYC_PREFILL_FIELDS = [
  'pan_number',
  'aadhaar_number',
  'gst_number',
  'passport_number',
  'driving_license',
  'date_of_birth',
  'gender',
  'pincode',
] as const;

type KycPrefillField = (typeof KYC_PREFILL_FIELDS)[number];

function isPrefillField(name: string): name is KycPrefillField {
  return (KYC_PREFILL_FIELDS as readonly string[]).includes(name);
}

/** What `POST /customers/kyc-extract/` answers. Candidates, never an answer. */
type KycExtractResponse = {
  document_type: string;
  extraction_status: 'not_attempted' | 'unavailable' | 'no_match' | 'unconfirmed';
  fields: Record<string, ExtractedField>;
  message?: string;
};

/** One field a reading offered, and what the form did with it. */
type ReadingField = {
  field: KycPrefillField;
  label: string;
  candidates: string[];
  /** `applied` -- the box was empty and now holds candidates[0].
   *  `kept`    -- the box already held something typed, which was NOT touched. */
  outcome: 'applied' | 'kept';
};

/** What happened when one queued document was read, per document. */
type DocumentReading = {
  key: string;
  fileName: string;
  status: 'reading' | 'read' | 'nothing' | 'failed';
  message: string;
  fields: ReadingField[];
  /** Read off the card but with no input on this form to put it in. */
  unusable: string[];
};

/**
 * One document chosen before the contact exists.
 *
 * `key` rather than the File itself: two files can share a name, a File is not
 * a stable React key, and the same file may legitimately be queued twice under
 * two document types.
 */
type PendingKycDocument = {
  key: string;
  file: File;
  documentType: string;
  status: 'queued' | 'uploading' | 'uploaded' | 'failed';
  error?: string;
};

function documentTypeLabel(value: string) {
  return CUSTOMER_KYC_DOCUMENT_TYPES.find((option) => option.value === value)?.label ?? value;
}

/**
 * Why a failed upload needs its own message extractor: the documents endpoint
 * answers a plain DRF field-error body (`{"file": ["Unsupported file type."]}`)
 * which `getErrorMessage` has no branch for -- it would fall through to axios's
 * own "Request failed with status code 400", which tells the user nothing about
 * the file they picked. Same shape as `avatarUploadErrorMessage`.
 */
function kycUploadErrorMessage(error: unknown): string {
  const data = (error as { response?: { data?: unknown } } | undefined)?.response?.data;
  if (data && typeof data === 'object') {
    for (const key of ['file', 'document_type', 'detail', 'non_field_errors']) {
      const value = (data as Record<string, unknown>)[key];
      if (typeof value === 'string' && value) return value;
      if (Array.isArray(value) && typeof value[0] === 'string' && value[0]) return value[0];
    }
  }
  return getErrorMessage(error);
}

/**
 * Item 4: attach KYC documents while creating the contact, not only afterwards.
 *
 * The documents cannot go first. `CustomerDocument.customer` is a non-nullable
 * FK and the upload route is nested under a saved pk resolved by
 * `get_object_or_404`, so a POST before the contact exists is a 404 before the
 * body is even read. This is therefore the same shape as the profile picture
 * above (queue #230): hold the files here, create the contact, then upload each
 * one to the endpoint that already exists.
 *
 * The part that is NOT cosmetic is the failure display. If the contact is
 * created and the second of three documents fails, the user has to be told
 * WHICH one and be able to send it again -- counting failures ("2 of 3
 * uploaded") leaves them hunting for which scan to find a second time, and
 * swallowing it entirely is this codebase's most-repeated bug. The contact is
 * never rolled back: it was created, that half genuinely worked, and deleting
 * it to tidy up would throw away the typed record too.
 */
function NewContactKycSection({
  documents,
  readings,
  onQueue,
  onRemove,
  onRetryFailed,
  onContinueWithout,
  onUseCandidate,
  isUploading,
  contactCreated,
}: {
  documents: PendingKycDocument[];
  readings: DocumentReading[];
  onQueue: (documentType: string, file: File) => void;
  onRemove: (key: string) => void;
  onRetryFailed: () => void;
  onContinueWithout: () => void;
  onUseCandidate: (field: KycPrefillField, value: string) => void;
  isUploading: boolean;
  contactCreated: boolean;
}) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [documentType, setDocumentType] = useState<string>(CUSTOMER_KYC_DOCUMENT_TYPES[0].value);

  const failed = documents.filter((document) => document.status === 'failed');

  return (
    <div className="mb-6 rounded-lg border border-dashed border-gray-300 p-4 dark:border-gray-600">
      {/* B1: this sits INSIDE Identity & Tax Details rather than in a card of
          its own at the top of the form, because the document and the fields it
          fills are one job. Attaching a PAN card reads it straight away and
          drops the candidates into the boxes below. */}
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">KYC Documents</h3>
        <p className="mt-0.5 text-xs text-gray-500">
          Optional. We read each one as you attach it and offer what we find for the fields below —
          nothing is saved until you press Create. The files themselves are uploaded once the contact
          exists.
        </p>
      </div>

      {/* Once the contact exists the queue is settled: what is left to do is
          retry what failed, not add more. Further documents belong on the
          contact's own KYC tab, where the extraction review lives. */}
      {!contactCreated && (
        <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,14rem)_1fr] sm:items-end">
          <Select
            id="new-contact-kyc-type"
            name="new-contact-kyc-type"
            data-testid="new-contact-kyc-type"
            label="Document type"
            value={documentType}
            onChange={(e) => setDocumentType(e.target.value)}
            options={CUSTOMER_KYC_DOCUMENT_TYPES.map((option) => ({
              value: option.value,
              label: option.label,
            }))}
          />
          <div>
            <label htmlFor="new-contact-kyc-file" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              File
            </label>
            <input
              id="new-contact-kyc-file"
              name="new-contact-kyc-file"
              data-testid="new-contact-kyc-file"
              ref={fileInputRef}
              type="file"
              accept="image/*,.pdf"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                onQueue(documentType, file);
                // Clear the control so the same file can be picked again under
                // a different document type, and so the row below is the only
                // record of what is queued.
                if (fileInputRef.current) fileInputRef.current.value = '';
              }}
              className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm file:mr-3 file:rounded file:border-0 file:bg-primary-600 hover:file:bg-primary-700 dark:file:bg-primary-500 dark:hover:file:bg-primary-400 file:px-3 file:py-1 file:text-sm file:font-medium file:text-white"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              PDF or a photo (JPG, PNG, WEBP, HEIC), up to 10 MB. Saved with the contact when you press Create.
            </p>
          </div>
        </div>
      )}

      {documents.length === 0 ? (
        <p className="py-2 text-sm text-gray-500 dark:text-gray-400">No documents queued.</p>
      ) : (
        <div className="space-y-2">
          {documents.map((document) => (
            <div
              key={document.key}
              data-testid={
                document.status === 'failed'
                  ? `kyc-upload-failed-${document.file.name}`
                  : 'new-contact-kyc-row'
              }
              className={
                document.status === 'failed'
                  ? 'flex items-start justify-between gap-3 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3'
                  : 'flex items-start justify-between gap-3 rounded-lg border border-gray-200 dark:border-gray-700 p-3'
              }
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-gray-900 dark:text-white">{document.file.name}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{documentTypeLabel(document.documentType)}</p>
                {document.status === 'failed' && (
                  <p className="mt-1 text-xs text-red-700 dark:text-red-300">Did not upload — {document.error}</p>
                )}
              </div>
              <div className="flex flex-shrink-0 items-center gap-2">
                {document.status === 'uploading' && <span className="text-xs text-gray-500 dark:text-gray-400">Uploading…</span>}
                {document.status === 'uploaded' && (
                  <span className="flex items-center gap-1 text-xs text-green-700 dark:text-green-300">
                    <CheckCircleIcon className="h-4 w-4" />
                    Uploaded
                  </span>
                )}
                {/* Only what has not landed can be dropped. Removing a row that
                    already uploaded here would say "removed" and leave the
                    document on the contact -- the exact lie this feature is
                    written to avoid. */}
                {(document.status === 'queued' || document.status === 'failed') && !isUploading && (
                  <button
                    type="button"
                    onClick={() => onRemove(document.key)}
                    className="rounded p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-red-600 dark:hover:text-red-400"
                    aria-label={`Remove ${document.file.name}`}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {readings.length > 0 && (
        <div className="mt-4 space-y-3" data-testid="kyc-readings">
          {readings.map((reading) => (
            <div
              key={reading.key}
              data-testid={`kyc-reading-${reading.fileName}`}
              className={
                reading.status === 'failed'
                  ? 'rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-900/20'
                  : 'rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800/40'
              }
            >
              <div className="flex items-start gap-2">
                <SparklesIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-gray-900 dark:text-gray-100">
                    {reading.fileName}
                  </p>
                  {/* Never silence. "Could not read it" and "read nothing" are
                      different answers, and a blank panel would read as
                      "there was nothing on the card". */}
                  <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-300">{reading.message}</p>

                  {reading.fields.length > 0 && (
                    <div className="mt-2 space-y-1.5">
                      {reading.fields.map((field) => (
                        <div key={field.field} className="text-xs">
                          <span className="font-medium text-gray-700 dark:text-gray-200">
                            {field.label}:
                          </span>{' '}
                          <span className="font-mono text-gray-900 dark:text-white">
                            {field.candidates[0]}
                          </span>{' '}
                          {field.outcome === 'applied' ? (
                            <span className="text-green-700 dark:text-green-400">— filled in below</span>
                          ) : (
                            <span className="text-gray-500">
                              — you had already typed something, so it was left alone
                            </span>
                          )}
                          {/* The rival readings, exactly as the contact's own
                              KYC panel offers them: tesseract confuses 0/O and
                              8/B constantly, and picking is one click. */}
                          {(field.candidates.length > 1 || field.outcome === 'kept') && (
                            <span className="ml-1 inline-flex flex-wrap items-center gap-1 align-middle">
                              {field.candidates.map((candidate) => (
                                <button
                                  key={candidate}
                                  type="button"
                                  onClick={() => onUseCandidate(field.field, candidate)}
                                  className="rounded border border-amber-300 dark:border-amber-800 bg-white dark:bg-gray-800 px-1.5 py-0.5 font-mono text-[11px] text-amber-900 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900/30"
                                >
                                  Use {candidate}
                                </button>
                              ))}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {reading.unusable.length > 0 && (
                    <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                      Also read, but this form has no field for it: {reading.unusable.join(', ')}.
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {contactCreated && failed.length > 0 && (
        <div className="mt-4 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3">
          <div className="flex items-start gap-2">
            <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500" />
            <div>
              <p className="text-sm font-medium text-red-800 dark:text-red-300">
                The contact was created, but {failed.length}{' '}
                {failed.length === 1 ? 'document' : 'documents'} did not upload.
              </p>
              <p className="mt-1 text-xs text-red-700 dark:text-red-300">
                Each one is named above with the reason. Nothing was lost from the contact itself — send them
                again, or continue and attach them from the contact's KYC tab later.
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="primary"
                  leftIcon={<ArrowPathIcon className="h-4 w-4" />}
                  onClick={onRetryFailed}
                  disabled={isUploading}
                >
                  {isUploading ? 'Uploading…' : `Retry ${failed.length === 1 ? 'it' : `all ${failed.length}`}`}
                </Button>
                <Button size="sm" variant="outline" onClick={onContinueWithout} disabled={isUploading}>
                  Continue to the contact
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * B2: the City control.
 *
 * WHAT IT REPLACES: a plain text input. `Customer.city` is a FK into the SEEDED
 * `geography.City` table, and the server resolved whatever was typed by name --
 * answering `None`, with no error and a 201, for anything it could not match.
 * 292 production contacts carry an address and no city. The picker posts an id,
 * so the thing chosen is the thing stored.
 *
 * THE THREE STATES ARE DELIBERATE. A lookup that fails must not render as an
 * empty dropdown: an empty dropdown says "this contact has no city", which is a
 * claim about the record rather than about the request. So a failure says it
 * failed, keeps the value the form already holds (it is still submitted
 * untouched), and names the city that is on file.
 */
/** Sentinel for the "clear this" row; see the option list below. */
const NO_CITY = '__no_city__';

function CityPicker({
  value,
  onChange,
  error,
  savedCityName,
}: {
  value: string;
  onChange: (value: string) => void;
  error?: string;
  savedCityName?: string | null;
}) {
  const cities = useCityLookup();
  const states = useStateLookup();

  const stateNames = new Map((states.data ?? []).map((state) => [state.id, state.name]));
  const nameCounts = new Map<string, number>();
  (cities.data ?? []).forEach((city) => {
    nameCounts.set(city.name, (nameCounts.get(city.name) ?? 0) + 1);
  });

  const options = (cities.data ?? []).map((city) => {
    // Two "New Delhi" rows exist in the seeded table, in different states.
    // Qualifying only the ambiguous ones keeps the list readable.
    const stateName = stateNames.get(city.state);
    const ambiguous = (nameCounts.get(city.name) ?? 0) > 1;
    return {
      value: city.id,
      label: ambiguous && stateName ? `${city.name}, ${stateName}` : city.name,
    };
  });

  if (cities.isLoading) {
    return (
      <div className="w-full">
        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">City</label>
        <div className="rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm text-gray-400">
          Loading cities…
        </div>
      </div>
    );
  }

  if (cities.isError) {
    return (
      <div className="w-full">
        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">City</label>
        <div className="rounded-lg border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          <p data-testid="city-lookup-error">The list of cities could not be loaded.</p>
          <p className="mt-1 text-xs">
            {savedCityName
              ? `This contact's city is ${savedCityName}; it has not been changed. Try again to pick a different one.`
              : 'Try again — saving now will leave the city blank.'}
          </p>
          <button
            type="button"
            onClick={() => cities.refetch()}
            className="mt-2 rounded border border-red-300 dark:border-red-800 bg-white dark:bg-gray-800 px-2 py-1 text-xs font-medium text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/30"
          >
            {cities.isFetching ? 'Retrying…' : 'Try again'}
          </button>
        </div>
      </div>
    );
  }

  // A stored city the lookup does not list (deactivated since, most likely).
  // Adding it keeps the box showing the truth instead of falling back to the
  // "Select..." placeholder, which would read as "no city on file".
  if (value && !options.some((option) => option.value === value)) {
    options.unshift({ value, label: savedCityName || 'City currently on file' });
  }

  return (
    <SearchableSelect
      id="customer-city"
      label="City"
      // The sentinel is not '' -- an option whose value is '' would match an
      // empty form and render as "No city" where the placeholder belongs.
      options={value ? [{ value: NO_CITY, label: 'No city' }, ...options] : options}
      value={value}
      onChange={(next) => onChange(next === NO_CITY ? '' : next)}
      placeholder="Search for a city..."
      error={error}
      helperText="Pick from the list — a city we do not hold cannot be saved."
    />
  );
}

/**
 * `avatar_image` rides on `CustomerUpdateSerializer`/`CustomerDetailSerializer`
 * but not on the shared `Customer` type in `types/index.ts`, which is outside
 * this task's file domain.
 */
type CustomerWithAvatar = { avatar?: string | null; avatar_image?: ContactAvatarImage | null };

export function CustomerFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isEditing = Boolean(id);

  const { data: customer, isLoading: customerLoading } = useCustomer(id!);
  const createMutation = useCreateCustomer();
  const updateMutation = useUpdateCustomer();

  const [serverErrors, setServerErrors] = useState<string[]>([]);
  // Queue #230: the picture chosen on the Add Contact screen, held until the
  // create returns an id to post it against.
  const [pendingPhoto, setPendingPhoto] = useState<File | null>(null);
  // Item 4: the KYC documents chosen on the Add Contact screen, held until the
  // create returns an id to hang them off. `createdCustomerId` is what keeps a
  // partial failure recoverable -- the contact exists from that point on, so the
  // form must stop offering to create it again and start offering the retry.
  const [pendingDocuments, setPendingDocuments] = useState<PendingKycDocument[]>([]);
  const [createdCustomerId, setCreatedCustomerId] = useState<string | null>(null);
  const [isUploadingDocuments, setIsUploadingDocuments] = useState(false);
  // B1: what each queued document was read as, before the contact exists. This
  // is display + provenance only -- the VALUES live in the form fields, where a
  // person can change them, and reach the record through the ordinary submit.
  const [readings, setReadings] = useState<DocumentReading[]>([]);
  const queryClient = useQueryClient();

  // F2: mirrors the server's own gate on the avatar action. It falls to
  // `CustomerViewSet.get_permissions`' default branch -- `IsBrokerEmployee`,
  // i.e. super_admin/country_admin/broker_admin/broker_employee -- so a POSP,
  // who App.tsx does route to this screen, would get a 403 from the upload.
  // Same shape as CustomerDetailPage's `canManageLogin` role check.
  // B4: both the avatar and the KYC document writes are contacts Edit on the
  // API (`avatar` and the `CustomerDocumentViewSet` child), so both controls
  // also need that cell.
  const canEditContactsRight = useCan('contacts', 'edit');
  const isBrokerStaff =
    user?.role === 'super_admin' ||
    user?.role === 'country_admin' ||
    user?.role === 'broker_admin' ||
    user?.role === 'broker_employee';
  const canEditPhoto = isBrokerStaff && canEditContactsRight;

  // Item 4. Wider than `canEditPhoto` at one end and narrower at the other, and
  // both differences are deliberate:
  //
  // * a POSP IS included. `CustomerDocumentViewSet` admits posp/super_posp, and
  //   `perform_create` on the contact stamps `posp = <creator>`, so a contact a
  //   POSP has just created is inside their own scope and the upload resolves.
  //   (The avatar action has no such branch, which is why the picture control
  //   stays hidden from them.)
  // * a CUSTOMER is excluded. A self-service create does not link
  //   `Customer.user`, and `scoped_customer_queryset` matches a customer on
  //   `user=` -- so their own upload would 404. Offering the control would be
  //   offering a guaranteed failure.
  const canAttachKycDocuments =
    (isBrokerStaff || user?.role === 'posp' || user?.role === 'super_posp') && canEditContactsRight;

  const {
    register,
    handleSubmit,
    reset,
    setError,
    setValue,
    getValues,
    watch,
    clearErrors,
    formState: { errors },
  } = useForm<CustomerFormData>({
    resolver: allowUnchangedEmails(zodResolver(customerSchema), () => ({ email: customer?.email })),
    defaultValues: {
      customer_type: 'individual',
      segment: 'standard',
    },
  });

  const customerType = watch('customer_type');
  const basicFields = getBasicFields(customerType);
  const identityFields = getIdentityFields(customerType);
  // B2: `city_name` has been on CustomerDetailSerializer all along and nothing
  // read it. It is what the City control falls back to when the city lookup
  // cannot be fetched -- so a failed request never renders as "no city".
  const savedCityName = (customer as (typeof customer & { city_name?: string | null }) | undefined)
    ?.city_name;

  // When customer_type changes: (1) clear any field that's no longer part of
  // the new type's Basic/Identity config, and its lingering validation error,
  // so stale data/errors from a previous type can't ride into submission or
  // hang around on an unmounted input; (2) salutation stays visible across
  // every type but its option set is type-specific (section 2 of the brief)
  // -- a value valid for the old type may not exist in the new type's list,
  // so it's cleared explicitly rather than relying on pass (1).
  useEffect(() => {
    const visible = new Set<CustomerFieldKey>([...basicFields, ...identityFields]);
    const hiddenFields = ALL_TOGGLEABLE_FIELDS.filter((field) => !visible.has(field));
    if (hiddenFields.length > 0) {
      // Clear to undefined (not ''): gender/marital_status are enum fields
      // whose zod schema would reject an empty string, which would then block
      // submission on a field the user can no longer see. undefined is valid
      // for every one of these optional fields.
      hiddenFields.forEach((field) => setValue(field, undefined));
      clearErrors(hiddenFields);
    }

    const allowedSalutations = new Set(salutationCodesFor(customerType));
    const currentSalutation = getValues('salutation');
    if (currentSalutation && !allowedSalutations.has(currentSalutation)) {
      setValue('salutation', undefined);
      clearErrors('salutation');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerType]);

  useEffect(() => {
    if (customer) {
      // Legacy data bridge: before Wave D1b, Corporate customers had no
      // dedicated Company Name input -- the form reused `first_name` (labeled
      // "Company / Firm Name") to satisfy the backend's required first_name
      // column, leaving the real `company_name` column blank. Fall back to
      // first_name so existing Corporate records don't show an empty Company
      // Name field the first time they're opened in the new form.
      const legacyCompanyName = customer.customer_type === 'corporate' ? customer.first_name : undefined;

      reset({
        customer_type: customer.customer_type,
        segment: customer.segment,
        salutation: customer.salutation || undefined,
        first_name: customer.first_name,
        middle_name: customer.middle_name || undefined,
        last_name: customer.last_name,
        date_of_birth: customer.date_of_birth || undefined,
        gender: customer.gender || undefined,
        marital_status: (customer.marital_status as 'single' | 'married' | 'divorced' | 'widowed') || undefined,
        occupation: customer.occupation || undefined,
        annual_income: customer.annual_income != null ? String(customer.annual_income) : undefined,
        company_name: customer.company_name || legacyCompanyName || undefined,
        company_type: customer.company_type || undefined,
        incorporation_date: customer.incorporation_date || undefined,
        cin_number: customer.cin_number || undefined,
        industry: customer.industry || undefined,
        nature_of_business: customer.nature_of_business || undefined,
        annual_turnover: customer.annual_turnover != null ? String(customer.annual_turnover) : undefined,
        num_employees: customer.num_employees != null ? String(customer.num_employees) : undefined,
        website: customer.website || undefined,
        email: customer.email || '',
        phone: customer.phone,
        alternate_phone: customer.alternate_phone || undefined,
        pan_number: customer.pan_number || undefined,
        aadhaar_number: customer.aadhaar_number || undefined,
        gst_number: customer.gst_number || undefined,
        passport_number: customer.passport_number || undefined,
        driving_license: customer.driving_license || undefined,
        address_line1: customer.address_line1 || undefined,
        address_line2: customer.address_line2 || undefined,
        city: customer.city || undefined,
        state: customer.state || undefined,
        pincode: customer.pincode || undefined,
      });
    }
  }, [customer, reset]);

  const handleError = (error: unknown) => {
    setServerErrors(applyServerErrors(error, setError));
  };

  // ── Item 4: the queued KYC documents ──────────────────────────────────────

  const queueDocument = (documentType: string, file: File) => {
    // A random suffix, not just the array length: the length is read from THIS
    // render, so two files queued before it re-renders would share a key -- and
    // `markDocument` patches by key, so both rows would then move together.
    const key = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}-${file.name}`;
    setPendingDocuments((previous) => [
      ...previous,
      { key, file, documentType, status: 'queued' },
    ]);
    // B1: read it NOW, while the form is still on screen and empty. The
    // document itself cannot be sent yet (its route is nested under a contact
    // id that does not exist), so this is a separate, stateless read.
    void readQueuedDocument(key, documentType, file);
  };

  const removeQueuedDocument = (key: string) => {
    setPendingDocuments((previous) => previous.filter((document) => document.key !== key));
    setReadings((previous) => previous.filter((reading) => reading.key !== key));
  };

  /**
   * Put one candidate into its form field.
   *
   * `setValue` is generic per field NAME, so a union of names makes TypeScript
   * demand a value assignable to every member at once. The cast is the
   * narrowing it cannot do here; the values themselves are checked below
   * (`gender` in particular, whose zod schema is a closed enum).
   */
  const applyCandidate = (field: KycPrefillField, value: string) => {
    if (field === 'gender' && !['male', 'female', 'other'].includes(value)) return;
    setValue(field as 'pan_number', value, { shouldDirty: true });
  };

  /**
   * Ask the server what it can read off a document, and offer it to the form.
   *
   * NOTHING IS WRITTEN ANYWHERE BY THIS. `POST /customers/kyc-extract/` stores
   * no document, no bytes and no Customer column -- it hands back candidates,
   * they land in the on-screen inputs, and the broker's own submit is still
   * what puts a value on the record. That is what keeps `confirm-extraction`'s
   * stated invariant true: a misread never becomes a stored compliance fact
   * without a person's name against it.
   *
   * A failure here is SHOWN, not swallowed. The document stays queued and will
   * still be uploaded with the contact; all that is lost is the convenience,
   * and the panel says so rather than leaving an empty box that reads as "there
   * was nothing on the card".
   */
  const readQueuedDocument = async (key: string, documentType: string, file: File) => {
    const label = documentTypeLabel(documentType);
    setReadings((previous) => [
      ...previous.filter((reading) => reading.key !== key),
      {
        key,
        fileName: file.name,
        status: 'reading',
        message: `Reading this ${label}…`,
        fields: [],
        unusable: [],
      },
    ]);

    const record = (patch: Partial<DocumentReading>) =>
      setReadings((previous) =>
        previous.map((reading) => (reading.key === key ? { ...reading, ...patch } : reading))
      );

    let payload: KycExtractResponse;
    try {
      const form = new FormData();
      form.append('document_type', documentType);
      form.append('file', file);
      const { data } = await api.post<KycExtractResponse>('/customers/kyc-extract/', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        // OCR runs in-request, on purpose -- the whole point is that the
        // candidates appear while the uploader is still looking at the form.
        timeout: 180000,
      });
      payload = data;
    } catch (error) {
      record({
        status: 'failed',
        message: `We could not read this one — ${kycUploadErrorMessage(error)}. Type the details in yourself; the file is still queued and will be attached to the contact.`,
      });
      return;
    }

    const fields = payload?.fields ?? {};
    const names = Object.keys(fields);
    if (names.length === 0) {
      record({
        status: 'nothing',
        message:
          payload?.message ??
          'Nothing was read from this document. Type the details in yourself.',
      });
      return;
    }

    // Only fields this form is SHOWING for the chosen customer type. Filling a
    // hidden input would submit a value the user was never given a chance to
    // check -- and the type-switch effect would wipe it anyway.
    //
    // Read NOW rather than closed over from the render that fired the request:
    // OCR takes seconds and the Customer Type dropdown is one click away, so
    // the set that mattered when the file was picked may not be the set that
    // is on screen when the answer lands.
    const currentType = getValues('customer_type');
    const visible = new Set<string>([
      ...getBasicFields(currentType),
      ...getIdentityFields(currentType),
      'pincode',
    ]);

    const applied: ReadingField[] = [];
    const unusable: string[] = [];

    names.forEach((name) => {
      const field = fields[name];
      const best = field?.candidates?.[0];
      if (!best) return;
      if (!isPrefillField(name) || !visible.has(name)) {
        unusable.push(KYC_FIELD_LABELS[name] ?? name.replace(/_/g, ' '));
        return;
      }
      const current = getValues(name);
      const alreadyTyped = typeof current === 'string' && current.trim().length > 0;
      if (!alreadyTyped) applyCandidate(name, best);
      applied.push({
        field: name,
        label: KYC_FIELD_LABELS[name] ?? name.replace(/_/g, ' '),
        candidates: field.candidates.slice(0, 5),
        outcome: alreadyTyped ? 'kept' : 'applied',
      });
    });

    const filled = applied.filter((entry) => entry.outcome === 'applied').length;
    record({
      status: 'read',
      fields: applied,
      unusable,
      message:
        filled > 0
          ? `Read from this document — check each value against the card before saving.`
          : 'Read from this document, but every field already had something typed in, so nothing was changed.',
    });
  };

  const markDocument = (key: string, patch: Partial<PendingKycDocument>) =>
    setPendingDocuments((previous) =>
      previous.map((document) => (document.key === key ? { ...document, ...patch } : document))
    );

  /**
   * Upload `documents` against a contact that now exists. Returns how many
   * failed, so the caller can decide whether this create is finished.
   *
   * SEQUENTIAL on purpose. OCR runs in-request on the server (that is the whole
   * point of the feature -- the candidates are on screen while the uploader is
   * still looking at the form), so five parallel uploads are five concurrent
   * tesseract passes on one worker. The 180s client timeout in
   * `customerKycService.upload` exists for the same reason.
   *
   * Every failure is caught PER DOCUMENT and recorded against that document.
   * The catch is not a swallow: nothing here returns success on a failed
   * upload, and the reason is put on screen next to the file it belongs to.
   */
  const uploadPendingDocuments = async (customerId: string, documents: PendingKycDocument[]) => {
    if (documents.length === 0) return 0;
    setIsUploadingDocuments(true);
    let failures = 0;
    try {
      for (const document of documents) {
        markDocument(document.key, { status: 'uploading', error: undefined });
        try {
          await customerKycService.upload(customerId, {
            document_type: document.documentType,
            file: document.file,
          });
          markDocument(document.key, { status: 'uploaded', error: undefined });
        } catch (error) {
          failures += 1;
          markDocument(document.key, { status: 'failed', error: kycUploadErrorMessage(error) });
        }
      }
    } finally {
      setIsUploadingDocuments(false);
    }
    if (failures < documents.length) {
      // At least one landed, so anything cached about this contact's documents
      // is now stale. These are the keys `hooks/useKycDocuments.ts` actually
      // uses -- an invented key here would invalidate nothing and the KYC tab
      // we are about to navigate to would open on a stale (empty) list.
      queryClient.invalidateQueries({ queryKey: ['customerKycDocuments', customerId] });
      queryClient.invalidateQueries({ queryKey: ['customer', customerId] });
    }
    return failures;
  };

  const goToCreatedContact = () => {
    if (createdCustomerId) {
      navigate(`/customers/${createdCustomerId}`);
    } else {
      navigate('/contacts');
    }
  };

  const retryFailedDocuments = async () => {
    if (!createdCustomerId) return;
    const failed = pendingDocuments.filter((document) => document.status === 'failed');
    if (failed.length === 0) return;

    const stillFailing = await uploadPendingDocuments(createdCustomerId, failed);
    if (stillFailing === 0) {
      toast.success(failed.length === 1 ? 'Document uploaded.' : 'Documents uploaded.');
      navigate(`/customers/${createdCustomerId}`);
    } else {
      toast.error(
        `${stillFailing} ${stillFailing === 1 ? 'document' : 'documents'} still did not upload. ` +
          'The reason is shown against each one.'
      );
    }
  };

  const onSubmit = (data: CustomerFormData) => {
    setServerErrors([]);
    const isCorporate = data.customer_type === 'corporate';
    const cleanedData = {
      ...data,
      // Corporate has no visible First/Last Name input -- first_name is still
      // required by the backend model/serializer and drives full_name, so
      // mirror the Company Name value into it (see superRefine above, which
      // guarantees company_name is non-empty for Corporate before this runs).
      first_name: isCorporate ? data.company_name || data.first_name || '' : data.first_name || '',
      email: data.email || undefined,
      salutation: data.salutation || undefined,
      marital_status: data.marital_status || undefined,
      annual_income: data.annual_income || undefined,
      annual_turnover: data.annual_turnover || undefined,
      num_employees: data.num_employees || undefined,
      website: data.website || undefined,
      // DRF's DateField (unlike DecimalField) does not treat '' as null, so an
      // untouched/cleared date input would otherwise fail backend validation
      // with "Date has wrong format".
      date_of_birth: data.date_of_birth || undefined,
      incorporation_date: data.incorporation_date || undefined,
    };

    if (isEditing) {
      updateMutation.mutate(
        { id: id!, data: cleanedData },
        {
          onSuccess: () => navigate(`/customers/${id}`),
          onError: handleError,
        }
      );
    } else {
      createMutation.mutate(cleanedData, {
        onSuccess: async (newCustomer) => {
          // Item 4: from here on the contact EXISTS. Recording that first is
          // what stops the form offering to create it a second time while the
          // documents are still going up.
          if (newCustomer?.id) {
            setCreatedCustomerId(newCustomer.id);
          }
          // Queue #230: the contact now exists, so the picture the user chose
          // on this form has somewhere to go. A failure here must NOT lose the
          // contact that was just created successfully -- so it surfaces as a
          // warning and the navigation continues; the picture can be set from
          // the Edit screen, which is exactly where it used to have to be set.
          if (pendingPhoto && newCustomer?.id) {
            try {
              const form = new FormData();
              form.append('file', pendingPhoto);
              await api.post(`/customers/${newCustomer.id}/avatar/`, form, {
                headers: { 'Content-Type': 'multipart/form-data' },
                // Optimisation and rendition generation are synchronous
                // server-side, so a phone-camera JPEG can outlast the client's
                // 30s default.
                timeout: 180000,
              });
              queryClient.invalidateQueries({ queryKey: ['customers'] });
            } catch (err) {
              toast.error(`Contact created, but the photo did not upload: ${avatarUploadErrorMessage(err)}`);
            }
          }

          // Item 4: the queued KYC documents, now that there is a pk to nest
          // them under.
          if (pendingDocuments.length > 0 && !newCustomer?.id) {
            // No id means no endpoint to post to and therefore no retry worth
            // offering. Say so plainly and fall through to the navigation
            // below -- staying here would leave a live Create button on a
            // contact that already exists, which is how the same person ends up
            // in the book twice.
            toast.error(
              `The contact was created but came back without an id, so ${pendingDocuments.length} ` +
                'queued document(s) could not be uploaded. Attach them from the contact’s KYC tab.'
            );
          } else if (pendingDocuments.length > 0 && newCustomer?.id) {
            const failures = await uploadPendingDocuments(newCustomer.id, pendingDocuments);
            if (failures > 0) {
              toast.error(
                `Contact created, but ${failures} of ${pendingDocuments.length} documents did not upload. ` +
                  'They are listed below — retry them, or continue without them.'
              );
              // STAY on this screen. The retry lives here, and navigating away
              // would turn a recoverable partial failure into a silent one.
              return;
            }
          }

          if (user?.role === 'customer') {
            navigate('/policies');
          } else if (newCustomer?.id) {
            navigate(`/customers/${newCustomer.id}`);
          } else {
            // Defensive fallback: if the create response ever comes back
            // without an id, don't navigate to /customers/undefined.
            navigate('/contacts');
          }
        },
        onError: handleError,
      });
    }
  };

  if (isEditing && customerLoading) {
    return <ContentLoader />;
  }

  // Single per-field renderer shared by every customer_type -- which fields
  // appear (and in what order) is driven entirely by BASIC_FIELDS_BY_TYPE /
  // IDENTITY_FIELDS_BY_TYPE above, not by a per-type JSX branch.
  function renderField(key: CustomerFieldKey) {
    switch (key) {
      case 'salutation':
        return (
          <Select
            key={key}
            label="Salutation"
            options={salutationOptionsFor(customerType)}
            placeholder="Select..."
            error={errors.salutation?.message}
            {...register('salutation')}
          />
        );
      case 'first_name':
        return (
          <Input
            key={key}
            label={customerType === 'proprietorship' ? 'Proprietor Name' : 'First Name'}
            error={errors.first_name?.message}
            required
            {...register('first_name', { onChange: nameKeepOnChange })}
          />
        );
      case 'middle_name':
        return (
          <Input key={key} label="Middle Name" error={errors.middle_name?.message} {...register('middle_name')} />
        );
      case 'last_name':
        return (
          <Input
            key={key}
            label={customerType === 'proprietorship' ? "Proprietor's Last Name" : 'Last Name'}
            error={errors.last_name?.message}
            required
            {...register('last_name', { onChange: nameKeepOnChange })}
          />
        );
      case 'date_of_birth':
        return (
          <Input
            key={key}
            label="Date of Birth"
            type="date"
            error={errors.date_of_birth?.message}
            {...register('date_of_birth')}
          />
        );
      case 'gender':
        return (
          <Select
            key={key}
            label="Gender"
            options={genderOptions}
            placeholder="Select..."
            error={errors.gender?.message}
            {...register('gender')}
          />
        );
      case 'marital_status':
        return (
          <Select
            key={key}
            label="Marital Status"
            options={maritalStatusOptions}
            placeholder="Select..."
            error={errors.marital_status?.message}
            {...register('marital_status')}
          />
        );
      case 'occupation':
        return (
          <Input key={key} label="Occupation" error={errors.occupation?.message} {...register('occupation')} />
        );
      case 'annual_income':
        return (
          <Input
            key={key}
            label="Annual Income"
            type="number"
            inputMode="numeric"
            error={errors.annual_income?.message}
            {...register('annual_income')}
          />
        );
      case 'company_name':
        return (
          <Input
            key={key}
            label={customerType === 'corporate' ? 'Company Name' : 'Business Name'}
            error={errors.company_name?.message}
            required
            {...register('company_name')}
          />
        );
      case 'cin_number':
        return (
          <Input
            key={key}
            label="CIN"
            placeholder="U12345MH2020PLC123456"
            maxLength={21}
            error={errors.cin_number?.message}
            {...register('cin_number')}
          />
        );
      case 'incorporation_date':
        return (
          <Input
            key={key}
            label={customerType === 'corporate' ? 'Date of Incorporation' : 'Date of Establishment'}
            type="date"
            error={errors.incorporation_date?.message}
            {...register('incorporation_date')}
          />
        );
      case 'industry':
        return <Input key={key} label="Industry" error={errors.industry?.message} {...register('industry')} />;
      case 'nature_of_business':
        return (
          <Input
            key={key}
            label="Nature of Business"
            error={errors.nature_of_business?.message}
            {...register('nature_of_business')}
          />
        );
      case 'annual_turnover':
        return (
          <Input
            key={key}
            label="Annual Turnover"
            type="number"
            inputMode="numeric"
            error={errors.annual_turnover?.message}
            {...register('annual_turnover')}
          />
        );
      case 'num_employees':
        return (
          <Input
            key={key}
            label="Number of Employees"
            type="number"
            inputMode="numeric"
            min={0}
            step={1}
            error={errors.num_employees?.message}
            {...register('num_employees')}
          />
        );
      case 'website':
        return (
          <Input
            key={key}
            label="Website"
            type="url"
            placeholder="https://example.com"
            error={errors.website?.message}
            {...register('website')}
          />
        );
      case 'company_type':
        return (
          <Input key={key} label="Business Type" error={errors.company_type?.message} {...register('company_type')} />
        );
      case 'pan_number':
        return (
          <Input
            key={key}
            label="PAN Number"
            placeholder="ABCDE1234F"
            maxLength={10}
            error={errors.pan_number?.message}
            {...register('pan_number')}
          />
        );
      case 'aadhaar_number':
        return (
          <Input
            key={key}
            label="Aadhaar Number"
            inputMode="numeric"
            maxLength={12}
            onKeyDown={(e) => {
              if (!/[0-9]/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                e.preventDefault();
              }
            }}
            error={errors.aadhaar_number?.message}
            {...register('aadhaar_number', { onChange: digitsOnlyOnChange })}
          />
        );
      case 'gst_number':
        return (
          <Input
            key={key}
            label="GST Number"
            placeholder="22ABCDE1234F1Z5"
            maxLength={15}
            error={errors.gst_number?.message}
            {...register('gst_number')}
          />
        );
      case 'passport_number':
        return (
          <Input
            key={key}
            label="Passport Number"
            maxLength={20}
            error={errors.passport_number?.message}
            {...register('passport_number')}
          />
        );
      case 'driving_license':
        return (
          <Input
            key={key}
            label="Driving License"
            maxLength={20}
            error={errors.driving_license?.message}
            {...register('driving_license')}
          />
        );
      default:
        return null;
    }
  }

  return (
    <div>
      <div className="mb-4">
        <BackButton to="/contacts" />
      </div>
      <PageHeader
        title={isEditing ? 'Edit Customer' : 'Add Customer'}
        breadcrumbs={[
          { label: 'Customers', href: '/customers' },
          isEditing && customer
            ? { label: customer.full_name, href: `/customers/${id}` }
            : null,
          { label: isEditing ? 'Edit' : 'New' },
        ].filter(Boolean) as { label: string; href?: string }[]}
        actions={
          <Link to={isEditing ? `/customers/${id}` : '/customers'}>
            <Button variant="outline" leftIcon={<ArrowLeftIcon className="w-5 h-5" />}>
              Cancel
            </Button>
          </Link>
        }
      />

      <form onSubmit={handleSubmit(onSubmit)}>
        <ErrorSummary messages={serverErrors} />
        <div className="space-y-6">
          {/* Profile Picture — editing an existing contact only (see
              ContactPhotoCard). Saved immediately by its own action rather
              than by this form's submit, because the file goes to a different
              endpoint than the rest of the fields. */}
          {isEditing && customer && canEditPhoto && (
            <ContactPhotoCard
              customerId={customer.id}
              name={customer.full_name}
              image={(customer as typeof customer & CustomerWithAvatar).avatar_image}
            />
          )}
          {/* Queue #230: the same affordance on Add Contact, so a picture no
              longer requires creating the contact and reopening it in Edit. */}
          {!isEditing && canEditPhoto && (
            <NewContactPhotoCard
              file={pendingPhoto}
              onSelect={setPendingPhoto}
              onClear={() => setPendingPhoto(null)}
            />
          )}

          {/* Basic Information */}
          <Card>
            <CardHeader
              title="Basic Information"
              subtitle="Enter the customer's personal details"
              action={
                // kyc_status is auto-derived by the backend (Wave D1a) -- read-only
                // display only, this form never sets it.
                isEditing && customer ? (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500 dark:text-gray-400">KYC Status</span>
                    <Badge variant={getStatusBadgeVariant(customer.kyc_status)}>
                      {kycStatusLabel(customer.kyc_status)}
                    </Badge>
                  </div>
                ) : undefined
              }
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              <Select
                label="Customer Type"
                options={customerTypeOptions}
                error={errors.customer_type?.message}
                required
                {...register('customer_type')}
              />
              <Select
                label="Segment"
                options={segmentOptions}
                error={errors.segment?.message}
                {...register('segment')}
              />
              {basicFields.map((key) => renderField(key))}
            </div>
          </Card>

          {/* Contact Information */}
          <Card>
            <CardHeader title="Contact Information" subtitle="How can we reach this customer?" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Input
                label="Phone Number"
                type="tel"
                inputMode="numeric"
                placeholder="9999999999"
                maxLength={10}
                onKeyDown={(e) => {
                  if (!/[0-9]/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                error={errors.phone?.message}
                required
                {...register('phone', { onChange: digitsOnlyOnChange })}
              />
              <Input
                label="Email Address"
                type="email"
                placeholder="customer@example.com"
                error={errors.email?.message}
                {...register('email')}
              />
              <Input
                label="Alternate Phone"
                type="tel"
                inputMode="numeric"
                placeholder="9999999999"
                maxLength={10}
                onKeyDown={(e) => {
                  if (!/[0-9]/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                error={errors.alternate_phone?.message}
                {...register('alternate_phone', { onChange: digitsOnlyOnChange })}
              />
            </div>
          </Card>

          {/* Identity & Tax Details */}
          <Card>
            <CardHeader title="Identity & Tax Details" subtitle="KYC and tax identifiers" />
            {/* B1: the documents live HERE now, not in a card of their own at the
                top of the form. Attaching a PAN card reads it immediately and
                offers what it found for the very inputs underneath it, which is
                the whole request -- before this, the card was uploaded after the
                create and the broker typed the number in by hand anyway.
                Create-only: on the Edit screen the contact exists and the full
                KYC panel, with its confirm-and-save review, is on its detail
                page. */}
            {!isEditing && canAttachKycDocuments && (
              <NewContactKycSection
                documents={pendingDocuments}
                readings={readings}
                onQueue={queueDocument}
                onRemove={removeQueuedDocument}
                onRetryFailed={retryFailedDocuments}
                onContinueWithout={goToCreatedContact}
                onUseCandidate={applyCandidate}
                isUploading={isUploadingDocuments}
                contactCreated={Boolean(createdCustomerId)}
              />
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {identityFields.map((key) => renderField(key))}
            </div>
          </Card>

          {/* Address */}
          <Card>
            <CardHeader title="Address" subtitle="Customer's residential or business address" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Input
                label="Address Line 1"
                error={errors.address_line1?.message}
                {...register('address_line1')}
              />
              <Input
                label="Address Line 2"
                error={errors.address_line2?.message}
                {...register('address_line2')}
              />
              <CityPicker
                value={watch('city') ?? ''}
                // '' rather than undefined when cleared: on an edit, a key the
                // payload does not carry means "leave this alone", so mapping
                // "no city" to undefined would report a save and change
                // nothing. An empty string is what the serializer reads as a
                // clear.
                onChange={(next) => setValue('city', next, { shouldDirty: true })}
                error={errors.city?.message}
                savedCityName={savedCityName}
              />
              {/* B6: PIN code pre-fills State; State is always a dropdown of
                  every seeded state, never free text. City above stays the
                  FK-id `CityPicker` (a different subsystem, out of B6's
                  scope) and is not touched by the PIN lookup. */}
              <AddressFields
                pincode={watch('pincode') ?? ''}
                onPincodeChange={(value) => setValue('pincode', value, { shouldValidate: true, shouldDirty: true })}
                pincodeError={errors.pincode?.message}
                state={watch('state') ?? ''}
                onStateChange={(value) => setValue('state', value, { shouldValidate: true, shouldDirty: true })}
                stateError={errors.state?.message}
              />
            </div>
          </Card>

          {/* Submit Button */}
          <div className="flex justify-end gap-4">
            <Link to={isEditing ? `/customers/${id}` : '/customers'}>
              <Button variant="outline" type="button">
                Cancel
              </Button>
            </Link>
            {/* Item 4: once the contact has been created this form is no longer
                a create form, even though it is still on screen because a
                document upload needs retrying. Offering "Create Customer" again
                here would file a duplicate contact for the same person. */}
            {createdCustomerId ? (
              <Button type="button" onClick={goToCreatedContact} disabled={isUploadingDocuments}>
                Go to the contact
              </Button>
            ) : (
              <Button
                type="submit"
                isLoading={createMutation.isPending || updateMutation.isPending}
              >
                {isEditing ? 'Update Customer' : 'Create Customer'}
              </Button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}
